import { create } from "zustand";
import { persist } from "zustand/middleware";
import { mockFlags } from "@/lib/mock-flags";
import { login as apiLogin, refresh as apiRefresh, logout as apiLogout, type LoginResponse } from "../api";

/** Perfil do usuário do backoffice (admin_users.role). */
export type AdminRole = "super_admin" | "financial" | "support" | "auditor";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  twoFactorEnabled: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  /**
   * Bootstrap concluído: o refresh silencioso de carregamento (`initAuth`) já
   * resolveu. Enquanto `false`, o layout aguarda (não redireciona para /login),
   * evitando o bounce que perdia a rota em deep-link/refresh (bug #19).
   */
  bootstrapped: boolean;
  /** Access token — mantido só em memória (nunca persistido). */
  accessToken: string | null;
  user: AdminUser | null;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Renova o access token. O refresh token vive num cookie HttpOnly (enviado
   * automaticamente via `withCredentials`) — não há refresh token em JS/storage,
   * então nada a passar aqui. Retorna sucesso.
   */
  refreshSession: () => Promise<boolean>;
}

const mockUser: AdminUser = {
  id: "0190f100-0000-7000-8000-000000000001",
  name: "Equipe ImobGo",
  email: "admin@imobgo.app",
  role: "super_admin",
  twoFactorEnabled: false,
};

function applySession(set: (p: Partial<AuthState>) => void, r: LoginResponse) {
  set({
    isAuthenticated: true,
    accessToken: r.accessToken,
    user: r.user,
  });
}

// Renovação única em voo (single-flight): o refresh token (cookie HttpOnly) é
// ROTACIONADO no primeiro uso — dois POST /auth/refresh concorrentes derrubam a
// sessão (mesmo motivo do dedupe do `initAuth` abaixo). Compartilhada por TODOS
// os chamadores (interceptor axios e cliente SSE de notificações).
let refreshInFlight: Promise<boolean> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Em mock, já entra autenticado para o portal ser navegável sem backend.
      isAuthenticated: mockFlags.auth,
      // Mock não faz refresh; real só fica pronto após `initAuth`.
      bootstrapped: mockFlags.auth,
      accessToken: mockFlags.auth ? "mock-token" : null,
      user: mockFlags.auth ? mockUser : null,

      login: async (email, password, totpCode) => {
        const result = await apiLogin(email, password, totpCode);
        applySession(set, result);
      },

      logout: async () => {
        const { accessToken } = get();
        // Best-effort: revoga no servidor (cookie `rt` acompanha); não bloqueia o
        // logout local em erro. O access token autentica a revogação.
        try {
          await apiLogout(accessToken);
        } catch {
          /* ignore */
        }
        set({ isAuthenticated: false, accessToken: null, user: null });
      },

      refreshSession: async () => {
        refreshInFlight ??= (async () => {
          try {
            const result = await apiRefresh();
            applySession(set, result);
            return true;
          } catch {
            set({ isAuthenticated: false, accessToken: null, user: null });
            return false;
          } finally {
            refreshInFlight = null;
          }
        })();
        return refreshInFlight;
      },
    }),
    {
      name: "imobgo.backoffice.auth",
      // Nunca persistir tokens. Só o perfil (UX) — o refresh token vive no cookie
      // HttpOnly, fora do alcance do JavaScript.
      partialize: (s) => ({ user: s.user }),
    },
  ),
);

/** Helper: o usuário atual tem alguma das roles informadas? */
export function hasRole(...roles: AdminRole[]): boolean {
  const role = useAuthStore.getState().user?.role;
  return role ? roles.includes(role) : false;
}

/**
 * Bootstrap de sessão no carregamento: em modo real, se há um perfil lembrado mas
 * nenhum access token em memória (reload), tenta um refresh silencioso — o cookie
 * HttpOnly `rt` autentica a renovação. Sem cookie válido, segue deslogado.
 */
// Promessa única de bootstrap: o StrictMode (dev) e qualquer remontagem chamam
// `initAuth` mais de uma vez. Sem dedupe, disparávamos dois POST /auth/refresh
// concorrentes — e como o refresh token é rotacionado no primeiro uso, o segundo
// falhava (derrubando a sessão). Compartilhar a promessa garante um refresh só.
let bootstrapPromise: Promise<void> | null = null;

async function runBootstrap(): Promise<void> {
  const { accessToken, user, refreshSession } = useAuthStore.getState();
  if (user && !accessToken) {
    await refreshSession();
  }
  // Marca o fim do bootstrap (sucesso, falha ou nada a fazer) para liberar o layout.
  useAuthStore.setState({ bootstrapped: true });
}

export function initAuth(): Promise<void> {
  if (mockFlags.auth) return Promise.resolve(); // já entra bootstrapped: true
  bootstrapPromise ??= runBootstrap();
  return bootstrapPromise;
}
